import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { fetchMemberAccountLedger } from '../../../controllers/memberAccountController';
import { resolveDonationLogoInfo } from '../donationCardsLogoUtils';

const Row = ({ label, value }) => (
  <Stack direction="row" justifyContent="space-between" spacing={2}>
    <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>{label}</Typography>
    <Typography
      sx={{
        fontSize: '13px',
        color: '#444',
        textAlign: 'right',
        fontFamily: value && value !== '-' ? 'ui-monospace, Menlo, Consolas, monospace' : 'inherit',
        wordBreak: 'break-all',
      }}
    >
      {value || '-'}
    </Typography>
  </Stack>
);

const DonationLogoInfoSection = ({ detail, variant = 'card' }) => {
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    if (!detail?.purchasedByPersonId) {
      setLedgerRows([]);
      return;
    }
    let active = true;
    setLedgerLoading(true);
    fetchMemberAccountLedger({
      memberId: detail.purchasedByPersonId,
      sourceTypes: [3],
      pageNumber: 1,
      pageSize: 100,
      includeSpouse: false,
      includeEventGuests: false,
      includeMealGuests: false,
    })
      .then(({ data }) => {
        if (active) setLedgerRows(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setLedgerRows([]);
      })
      .finally(() => {
        if (active) setLedgerLoading(false);
      });
    return () => { active = false; };
  }, [detail?.id, detail?.purchasedByPersonId]);

  const logoInfo = useMemo(
    () => resolveDonationLogoInfo(detail, ledgerRows),
    [detail, ledgerRows],
  );

  if (!detail) return null;

  const content = (
    <Stack spacing={1.5}>
      {logoInfo.showPendingWarning && (
        <Alert severity="warning" sx={{ fontSize: '13px' }}>
          Logo entegrasyonu tamamlanamadı olabilir. Kayıt bekliyor durumunda; destek ile iletişime geçin.
        </Alert>
      )}
      {logoInfo.showManualNote && (
        <Alert severity="info" sx={{ fontSize: '13px' }}>
          Nakit ve havale kayıtları Logo fişi oluşturmaz. Logo yalnızca PayTR ile kredi kartı ödemelerinde otomatik işlenir.
        </Alert>
      )}
      {logoInfo.showLogoSection && (
        <>
          {ledgerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Stack spacing={1}>
              <Row label="Borç fişi (04)" value={logoInfo.debtSlipNo || 'Henüz yok'} />
              <Row label="Tahsilat fişi (08)" value={logoInfo.collectionSlipNo || 'Henüz yok'} />
              <Typography sx={{ fontSize: '12px', color: '#a3a3a3', mt: 0.5 }}>
                Cari: satın alan üye (.401) · Gelir: 603.003.106 · Tahsilat: 108.002.704
              </Typography>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );

  if (variant === 'inline') {
    return content;
  }

  return (
    <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <AccountBalanceIcon sx={{ color: '#0E4D94', fontSize: 20 }} />
          <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>Logo bilgisi</Typography>
        </Stack>
        {content}
      </CardContent>
    </Card>
  );
};

export default DonationLogoInfoSection;
